import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import type { TripType, CreateTripTypeInput, UpdateTripTypeInput } from '../types'

export const useTripTypes = () => {
  const [tripTypes, setTripTypes] = useState<TripType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load trip types from Supabase
  useEffect(() => {
    loadTripTypes()
  }, [])

  const loadTripTypes = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_types')
        .select('*')
        .order('name')

      if (error) throw error

      const mappedTripTypes: TripType[] = data.map(row => ({
        id: row.id.toString(),
        name: row.name,
        description: row.description,
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      }))

      setTripTypes(mappedTripTypes)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trip types')
    } finally {
      setLoading(false)
    }
  }

  const createTripType = async (input: CreateTripTypeInput) => {
    try {
      setError(null)
      
      // For now, use a temporary user_id - this will be replaced with auth
      const { data: { user } } = await supabase.auth.getUser()
      const userId = user?.id || 'temp-user-id'
      
      const { data, error } = await supabase
        .from('trip_types')
        .insert({
          user_id: userId,
          name: input.name,
          description: input.description,
        })
        .select()
        .single()

      if (error) throw error

      const newTripType: TripType = {
        id: data.id.toString(),
        name: data.name,
        description: data.description,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      }

      setTripTypes(prev => [...prev, newTripType])
      return newTripType
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create trip type'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updateTripType = async (input: UpdateTripTypeInput) => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_types')
        .update({
          name: input.name,
          description: input.description,
        })
        .eq('id', parseInt(input.id))
        .select()
        .single()

      if (error) throw error

      const updatedTripType: TripType = {
        id: data.id.toString(),
        name: data.name,
        description: data.description,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      }

      setTripTypes(prev => 
        prev.map(tripType => 
          tripType.id === input.id ? updatedTripType : tripType
        )
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update trip type'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const deleteTripType = async (id: string) => {
    try {
      setError(null)
      
      const { error } = await supabase
        .from('trip_types')
        .delete()
        .eq('id', parseInt(id))

      if (error) throw error

      setTripTypes(prev => prev.filter(tripType => tripType.id !== id))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete trip type'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  return {
    tripTypes,
    loading,
    error,
    createTripType,
    updateTripType,
    deleteTripType,
    refetch: loadTripTypes,
  }
}